import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import {
  HelcimCustomerResponse,
  TestPaymentService
} from '../services/test-payment.service';
import { OrganizationDto, OrganizationService } from '../services/organization.service';

interface RelatedLink {
  label: string;
  route: string;
}

@Component({
  selector: 'app-payment-services-stub-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">{{ title() }}</h1>
          <p class="page-sub">{{ subtitle() }}</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          @if (isCreateCustomerPage()) {
            <div class="placeholder-card">
              <h2>Create Customer</h2>
              <p>Customer create is now Organization-owned. Use the Organizations page create/update flow to manage Helcim customer sync.</p>

              <div class="form">
                <label class="field">
                  <span>Organization</span>
                  <select [(ngModel)]="organizationId" name="organizationId" (ngModelChange)="onOrganizationChange()" required>
                    <option [ngValue]="0">Select organization</option>
                    @for (organization of organizations; track organization.id) {
                      <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                    }
                  </select>
                </label>
                <label class="field"><span>Idempotency Key (string, optional)</span><input type="text" [(ngModel)]="idempotencyKey" name="idempotencyKey" maxlength="140" /></label>
                <label class="field"><span>Customer Code (string)</span><input type="text" [(ngModel)]="customerCode" name="customerCode" maxlength="64" required /></label>
                <label class="field"><span>Contact Name (string)</span><input type="text" [(ngModel)]="contactName" name="contactName" maxlength="120" /></label>
                <label class="field"><span>Business Name (string)</span><input type="text" [(ngModel)]="businessName" name="businessName" maxlength="140" /></label>
                <label class="field"><span>Cell Phone (string)</span><input type="text" [(ngModel)]="cellPhone" name="cellPhone" maxlength="32" /></label>

                <div class="section-title">Billing Address</div>
                <label class="field"><span>Name (string)</span><input type="text" [(ngModel)]="billingName" name="billingName" maxlength="120" required /></label>
                <label class="field"><span>Street 1 (string)</span><input type="text" [(ngModel)]="billingStreet1" name="billingStreet1" maxlength="120" required /></label>
                <label class="field"><span>Street 2 (string, optional)</span><input type="text" [(ngModel)]="billingStreet2" name="billingStreet2" maxlength="120" /></label>
                <label class="field"><span>City (string)</span><input type="text" [(ngModel)]="billingCity" name="billingCity" maxlength="80" required /></label>
                <label class="field"><span>State (string, optional)</span><input type="text" [(ngModel)]="billingProvince" name="billingProvince" maxlength="40" /></label>
                <label class="field"><span>Country (string, optional)</span><input type="text" [(ngModel)]="billingCountry" name="billingCountry" maxlength="40" /></label>
                <label class="field"><span>Postal Code (string)</span><input type="text" [(ngModel)]="billingPostalCode" name="billingPostalCode" maxlength="20" required /></label>
                <label class="field"><span>Phone (string, optional)</span><input type="text" [(ngModel)]="billingPhone" name="billingPhone" maxlength="32" /></label>
                <label class="field"><span>Email (string, optional)</span><input type="email" [(ngModel)]="billingEmail" name="billingEmail" maxlength="120" /></label>

                <div class="section-title">Shipping Address</div>
                <label class="field"><span>Name (string)</span><input type="text" [(ngModel)]="shippingName" name="shippingName" maxlength="120" required /></label>
                <label class="field"><span>Street 1 (string)</span><input type="text" [(ngModel)]="shippingStreet1" name="shippingStreet1" maxlength="120" required /></label>
                <label class="field"><span>Street 2 (string, optional)</span><input type="text" [(ngModel)]="shippingStreet2" name="shippingStreet2" maxlength="120" /></label>
                <label class="field"><span>City (string)</span><input type="text" [(ngModel)]="shippingCity" name="shippingCity" maxlength="80" required /></label>
                <label class="field"><span>State (string, optional)</span><input type="text" [(ngModel)]="shippingProvince" name="shippingProvince" maxlength="40" /></label>
                <label class="field"><span>Country (string, optional)</span><input type="text" [(ngModel)]="shippingCountry" name="shippingCountry" maxlength="40" /></label>
                <label class="field"><span>Postal Code (string)</span><input type="text" [(ngModel)]="shippingPostalCode" name="shippingPostalCode" maxlength="20" required /></label>
                <label class="field"><span>Phone (string, optional)</span><input type="text" [(ngModel)]="shippingPhone" name="shippingPhone" maxlength="32" /></label>
                <label class="field"><span>Email (string, optional)</span><input type="email" [(ngModel)]="shippingEmail" name="shippingEmail" maxlength="120" /></label>

                <label class="field field--full"><span>Helcim Customer Id</span><input type="text" [value]="helcimCustomerId" readonly /></label>

                <div class="actions">
                  <button type="button" class="btn btn--primary" [disabled]="true">
                    Create Customer (Moved to Organizations)
                  </button>
                </div>
              </div>

              @if (errorMessage) {
                <div class="result result--error">{{ errorMessage }}</div>
              }

              @if (createResult) {
                <div class="result result--ok">
                  <div><strong>Status:</strong> Customer created</div>
                  <div><strong>Customer Id:</strong> {{ createResult.customerId || 'N/A' }}</div>
                  <div><strong>Customer Code:</strong> {{ createResult.customerCode || 'N/A' }}</div>
                  <div><strong>Business Name:</strong> {{ createResult.businessName || 'N/A' }}</div>
                  <div><strong>HelcimCustomerId Sync:</strong> {{ createResult.customerId ? 'Updated' : 'No customer id returned' }}</div>
                </div>
              }
            </div>
          } @else {
            <div class="placeholder-card">
              <h2>Stub Placeholder</h2>
              <p>
                This page is planned for full implementation. Organization selectors, invoice inputs, and additional payment fields
                will be added as this operation is implemented.
              </p>
            </div>
          }

          @if (relatedLinks().length > 0) {
            <div class="related-card">
              <h3>Quick Access</h3>
              <div class="related-grid">
                @for (link of relatedLinks(); track link.route) {
                  <a class="quick-link" [routerLink]="link.route">{{ link.label }}</a>
                }
              </div>
            </div>
          }
        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.placeholder-card,.related-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);padding:1rem 1.1rem}.related-card{margin-top:1rem}.placeholder-card h2,.related-card h3{margin:0 0 .55rem}.placeholder-card p{margin:0;color:var(--color-muted)}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding-top:1rem}.section-title{grid-column:1 / -1;font-weight:600;margin-top:.35rem}.field{display:flex;flex-direction:column;gap:.35rem}.field--full{grid-column:1 / -1}.field input,.field select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.actions{grid-column:1 / -1;display:flex;justify-content:flex-start}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.result{margin-top:1rem;padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}.related-grid{display:grid;gap:.6rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-link{border:1px solid var(--color-border);border-radius:.65rem;padding:.55rem .75rem;text-decoration:none;color:var(--color-text);background:color-mix(in srgb,var(--color-surface-1) 75%,transparent)}.quick-link:hover{border-color:color-mix(in srgb,var(--color-accent) 35%,var(--color-border))}`]
})
export class PaymentServicesStubPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly title = computed(() => this.route.snapshot.data['title'] as string ?? 'Payment Services');
  readonly subtitle = computed(() => this.route.snapshot.data['subtitle'] as string ?? 'Planned payment operation page.');
  readonly relatedLinks = computed(() => this.route.snapshot.data['relatedLinks'] as RelatedLink[] ?? []);
  readonly isCreateCustomerPage = computed(() => this.route.snapshot.routeConfig?.path === 'payment-services/customer/create-customer');

  organizationId = 0;
  idempotencyKey = '';
  customerCode = '';
  contactName = '';
  businessName = '';
  cellPhone = '';
  billingName = '';
  billingStreet1 = '';
  billingStreet2 = '';
  billingCity = '';
  billingProvince = '';
  billingCountry = '';
  billingPostalCode = '';
  billingPhone = '';
  billingEmail = '';
  shippingName = '';
  shippingStreet1 = '';
  shippingStreet2 = '';
  shippingCity = '';
  shippingProvince = '';
  shippingCountry = '';
  shippingPostalCode = '';
  shippingPhone = '';
  shippingEmail = '';
  helcimCustomerId = '';
  organizations: OrganizationDto[] = [];

  isSubmitting = false;
  errorMessage = '';
  createResult: HelcimCustomerResponse | null = null;

  async ngOnInit(): Promise<void> {
    if (!this.isCreateCustomerPage()) {
      return;
    }

    await this.loadOrganizationsAsync();
  }

  onOrganizationChange(): void {
    const organization = this.organizations.find((item) => item.id === this.organizationId);
    this.customerCode = this.organizationId > 0 ? String(this.organizationId) : '';

    if (!organization) {
      return;
    }

    this.contactName = organization.contactName || organization.billingContactName || '';
    this.businessName = organization.legalName || '';
    this.billingName = organization.billingBusinessName || organization.legalName || '';
    this.billingStreet1 = organization.billingAddress1 || '';
    this.billingStreet2 = organization.billingAddress2 || '';
    this.billingCity = organization.billingCity || '';
    this.billingProvince = organization.billingState || '';
    this.billingCountry = organization.billingCountry || 'USA';
    this.billingPostalCode = organization.billingZipCode || '';
    this.billingPhone = organization.billingContactPhone || '';
    this.billingEmail = organization.billingContactEmail || '';

    this.shippingName = organization.shippingBusinessName || organization.legalName || '';
    this.shippingStreet1 = organization.shippingAddress1 || '';
    this.shippingStreet2 = organization.shippingAddress2 || '';
    this.shippingCity = organization.shippingCity || '';
    this.shippingProvince = organization.shippingState || '';
    this.shippingCountry = organization.shippingCountry || 'USA';
    this.shippingPostalCode = organization.shippingZipCode || '';
    this.shippingPhone = organization.shippingContactPhone || '';
    this.shippingEmail = organization.shippingContactEmail || organization.contactEmail || '';
  }

  private async loadOrganizationsAsync(): Promise<void> {
    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      this.organizations = isSiteAdmin
        ? await firstValueFrom(this.organizationService.getAll())
        : [await firstValueFrom(this.organizationService.getMyOrganization())];

      if (this.organizationId <= 0 && this.organizations.length === 1) {
        this.organizationId = this.organizations[0].id;
      }

      this.onOrganizationChange();
    } catch {
      this.organizations = [];
      this.errorMessage = 'Unable to load organizations.';
    }
  }

  async createCustomer(): Promise<void> {
    this.errorMessage = '';
    this.createResult = null;

    if (
      this.organizationId <= 0
      || !this.customerCode.trim()
      || !this.billingName.trim()
      || !this.billingStreet1.trim()
      || !this.billingCity.trim()
      || !this.billingPostalCode.trim()
      || !this.shippingName.trim()
      || !this.shippingStreet1.trim()
      || !this.shippingCity.trim()
      || !this.shippingPostalCode.trim()
    ) {
      this.errorMessage = 'Organization, customer code, and required billing/shipping fields are required.';
      return;
    }

    this.isSubmitting = true;

    try {
      const organization = this.organizations.find((item) => item.id === this.organizationId);

      const mappedContactName = organization?.contactName?.trim() || this.contactName.trim() || undefined;
      const mappedBusinessName = organization?.legalName?.trim() || this.businessName.trim() || undefined;
      const mappedBillingName = organization?.billingBusinessName?.trim() || this.billingName.trim();
      const mappedShippingName = organization?.shippingBusinessName?.trim() || this.shippingName.trim();
      const mappedCellPhone = organization?.billingContactPhone?.trim() || this.cellPhone.trim() || undefined;
      const mappedBillingPhone = organization?.billingContactPhone?.trim() || this.billingPhone.trim() || undefined;
      const mappedShippingPhone = organization?.shippingContactPhone?.trim() || this.shippingPhone.trim() || undefined;

      const response = await this.testPaymentService.createCustomer({
        organizationId: this.organizationId,
        idempotencyKey: this.idempotencyKey.trim() || undefined,
        customerCode: this.customerCode.trim(),
        contactName: mappedContactName,
        businessName: mappedBusinessName,
        cellPhone: mappedCellPhone,
        billingAddress: {
          name: mappedBillingName,
          street1: this.billingStreet1.trim(),
          street2: this.billingStreet2.trim() || undefined,
          city: this.billingCity.trim(),
          province: this.billingProvince.trim() || undefined,
          country: this.billingCountry.trim() || undefined,
          postalCode: this.billingPostalCode.trim(),
          phone: mappedBillingPhone,
          email: this.billingEmail.trim() || undefined
        },
        shippingAddress: {
          name: mappedShippingName,
          street1: this.shippingStreet1.trim(),
          street2: this.shippingStreet2.trim() || undefined,
          city: this.shippingCity.trim(),
          province: this.shippingProvince.trim() || undefined,
          country: this.shippingCountry.trim() || undefined,
          postalCode: this.shippingPostalCode.trim(),
          phone: mappedShippingPhone,
          email: this.shippingEmail.trim() || undefined
        }
      }).toPromise();

      if (!response) {
        throw new Error('Create customer request returned no data.');
      }

      this.createResult = response;
      this.helcimCustomerId = response.customerId || '';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Create customer request failed.';
    } finally {
      this.isSubmitting = false;
      this.changeDetectorRef.detectChanges();
    }
  }
}
